import { DEFAULT_VALUE } from "@supplier/constants";

/** 数量/重量字段统一支持的小数位数。 */
export const QUANTITY_DECIMAL_PRECISION = 6;

/** 去除小数末尾多余 0 和孤立小数点。 */
export const trimTrailingDecimalZeros = (value: string) => {
    return value.replace(/(\.\d*?[1-9])0+$/u, "$1").replace(/\.0*$/u, "");
};

/** 数字输入只保留第一个小数点，后续小数点按误输入忽略。 */
const normalizeDecimalPointInput = (value: string) => {
    const [integerValue = "", ...decimalValues] = value.split(".");
    if (!decimalValues.length) return integerValue;
    return `${integerValue}.${decimalValues.join("")}`;
};

/** 将数值按最多指定小数位做千分位展示。 */
const formatNumberWithMaxPrecision = (numVal: number, precision = QUANTITY_DECIMAL_PRECISION) => {
    return numVal.toLocaleString("en-US", {
        maximumFractionDigits: precision,
        useGrouping: true
    });
};

const DECIMAL_NUMBER_TEXT_REG = /^[+-]?(?:\d+|\d*\.\d+)$/u;

/** 按字符串给整数部分补千分位，避免超大数进入 Number 后丢精度。 */
const formatIntegerTextWithGrouping = (integerText: string) => {
    const normalizedInteger = integerText.replace(/^0+(?=\d)/u, "") || "0";
    return normalizedInteger.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

/** 字符串数量展示专用格式化，保留接口返回的小数精度。 */
const formatDecimalTextWithMaxPrecision = (value: string, precision = QUANTITY_DECIMAL_PRECISION) => {
    const normalizedValue = value.trim();
    if (!DECIMAL_NUMBER_TEXT_REG.test(normalizedValue)) return "";

    const sign = normalizedValue.startsWith("-") ? "-" : "";
    const unsignedValue = normalizedValue.replace(/^[+-]/u, "");
    const [integerText = "", decimalText] = unsignedValue.split(".");
    const formattedInteger = formatIntegerTextWithGrouping(integerText || "0");
    const formattedDecimal =
        decimalText === undefined ? "" : trimTrailingDecimalZeros(`.${decimalText.slice(0, precision)}`).slice(1);

    return `${sign}${formattedInteger}${formattedDecimal ? `.${formattedDecimal}` : ""}`;
};

/** 判断当前值是否来自正在编辑的输入框，用于输入态保留小数尾零。 */
const isActiveInputValue = (value: string) => {
    if (typeof document === "undefined") return false;
    const activeElement = document.activeElement;
    if (!(activeElement instanceof HTMLInputElement || activeElement instanceof HTMLTextAreaElement)) return false;
    const activeValue = activeElement.value.replace(/,/g, "");
    const currentValue = value.replace(/,/g, "");
    const normalizedActiveValue = normalizeDecimalPointInput(activeValue);
    // 输入 5.120 时，DOM 当前值可能仍是 5.12；数值等价即可判定为同一个正在编辑的输入框。
    return activeValue === currentValue || normalizedActiveValue === currentValue || Number(activeValue) === Number(currentValue);
};

/** 格式化手机号,12345678900 => 123 4567 8900 */
export const formatPhone = (phone?: string) => {
    if (!phone) return DEFAULT_VALUE;
    return phone.replace(/(\d{3})(\d{4})(\d{4})/g, "$1 $2 $3");
};

/** 格式化手机号,用于输入中 */
export const formatterPhone = (value: string) => {
    if (!value || value.length <= 3) return value;
    if (value.length > 3 && value.length <= 7) return `${value.slice(0, 3)} ${value.slice(3)}`;
    return `${value.slice(0, 3)} ${value.slice(3, 7)} ${value.slice(7)}`;
};

/** 格式化银行账号,用于输入中,6311111111122231234 => 631 1111 1111 2223 1234 */
export const formatterBankAccount = (account: string) => {
    // 前3位后加空格，后面每4位字符后加一个空格
    let str = "";
    for (const [i, char] of Array.from(account).entries()) {
        str += char;
        if (i === 2 || (i > 2 && (i - 2) % 4 === 0)) {
            str += " ";
        }
    }
    return str.trimEnd();
};

/** 格式化银行账号,6311111111122231234 => 631 1111 1111 2223 1234 */
export const formatBankAccount = (account?: string) => {
    if (!account) return DEFAULT_VALUE;
    return formatterBankAccount(account);
};

/**
 * 格式化数字显示 1234.1 => 1,234.1
 * @param num 数字
 * @param unit 单位
 */
export const fmtNum = (num?: number | string, unit?: string) => {
    // 空值判断：undefined/null直接返回默认值
    if (num === undefined || num === null) return DEFAULT_VALUE;
    if (typeof num === "string") {
        const formattedText = formatDecimalTextWithMaxPrecision(num);
        if (formattedText) return formattedText + (unit || "");
    }
    // 转数字+非数字判断：非有效数字返回默认值
    const numVal = Number(num);
    if (Number.isNaN(numVal)) return DEFAULT_VALUE;
    // 数量/重量类展示最多保留 6 位小数，且原生格式化会自动去除尾部 0。
    const formatStr = formatNumberWithMaxPrecision(numVal);
    // 拼接单位（无单位则空）返回
    return formatStr + (unit || "");
};

/**
 * 固定小数位格式化数量，适用于 PRD 明确要求保留尾零的统计字段。
 */
export const fmtNumFixed = (num?: number | string, precision = 2, unit?: string) => {
    if (num === undefined || num === null || num === "") return DEFAULT_VALUE;
    const numVal = Number(num);
    if (Number.isNaN(numVal)) return DEFAULT_VALUE;
    return (
        numVal.toLocaleString("en-US", {
            minimumFractionDigits: precision,
            maximumFractionDigits: precision,
            useGrouping: true
        }) + (unit || "")
    );
};

/**
 * 格式化金钱显示 1234.1 => 1,234.10
 * @param num 数字
 * @param unit 单位
 * @param format 小数点后位数
 */
export const fmtMoney = (num?: number | string, unit?: string, format: number = 2) => {
    // 1. 空值/非数字判断，返回默认值
    if (num === undefined || num === null) return DEFAULT_VALUE;
    const numStr = String(num).trim();
    if (numStr === "" || Number.isNaN(Number(numStr))) return DEFAULT_VALUE;

    // 2. 计算原数字的小数位数
    const decimalPlaces = numStr.includes(".") ? numStr.split(".")[1].length : 0;

    // 3. 智能计算最终保留的小数位（原逻辑完全不变）
    const decimals = format > 2 ? Math.min(Math.max(decimalPlaces, 2), format) : format;

    // 4. 原生实现「千分位+指定小数位」格式化
    const numVal = Number(numStr);
    const formatStr = numVal.toLocaleString("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        useGrouping: true // 强制千分位，与原逻辑一致
    });

    // 5. 拼接单位返回
    return formatStr + (unit || "");
};

/** 格式化输入框，数字显示 1234.1 => 1,234.1 */
const formatInputNum = (value: unknown, precision: number, preserveActiveDecimal: boolean) => {
    // 1. 非字符串/空字符串直接返回空（原逻辑前置判断）
    if (typeof value !== "string") return "";
    if (value === "") return ""; // 原value===undefined已被typeof过滤，无需重复判断
    // 2. 转数字校验：非有效数字返回空。输入框回显可能带千分位，校验前先去掉逗号。
    const normalizedValue = value.replace(/,/g, "");
    // 数量/重量输入过非法字符后继续输入小数点时，需要保留可继续编辑的中间态。
    if (preserveActiveDecimal && normalizedValue === "." && isActiveInputValue(normalizedValue)) return "0.";
    const numVal = Number(normalizedValue);
    if (Number.isNaN(numVal)) return "";

    const [integerValue = "", decimalValue] = normalizedValue.split(".");
    const integerNum = Number(integerValue || "0");
    const formattedInteger = integerNum.toLocaleString("en-US", { useGrouping: true });

    // 3. 数量/重量输入态保留完整小数串；普通输入框仍保持原公共格式化行为。
    if (preserveActiveDecimal && decimalValue !== undefined && isActiveInputValue(normalizedValue)) {
        return `${formattedInteger}.${decimalValue.slice(0, precision)}`;
    }

    const decimalTail = preserveActiveDecimal ? "" : (normalizedValue.match(/\.(0*)$/u)?.[0] ?? "");

    // 4. 失焦/展示态按最多精度格式化，去掉无意义尾零，兜底默认值（对齐原逻辑）。
    return formatNumberWithMaxPrecision(numVal, precision) + decimalTail || DEFAULT_VALUE;
};

/** 格式化输入框，数字显示 1234.1 => 1,234.1 */
export const fmtInputNum = (value: unknown) => {
    return formatInputNum(value, 2, false);
};

/** 数量/重量输入框格式化：最多 6 位小数，展示时去掉多余尾零。 */
export const fmtQuantityInputNum = (value: unknown) => {
    return formatInputNum(value, QUANTITY_DECIMAL_PRECISION, true);
};
